import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import {
  SwapHoriz,
  Calculate,
  Clear,
  CheckCircle,
  Business,
  Schedule,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, differenceInDays, startOfDay } from 'date-fns';
import { trackDateCalculation } from '../services/analytics.ts';

const DateCalculator: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [result, setResult] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  const calculateDays = () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      setResult(null);
      return;
    }

    const start = startOfDay(startDate);
    const end = startOfDay(endDate);
    
    const days = differenceInDays(end, start);
    setResult(days);
    setError('');

    // Track the calculation
    trackDateCalculation(startDate, endDate, days);
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setResult(null);
    setError('');
  };

  const swapDates = () => {
    if (startDate && endDate) {
      setStartDate(endDate);
      setEndDate(startDate);
      setResult(null);
      setError('');
    }
  };

  const getResultText = () => {
    if (result === null) return '';
    
    if (result === 0) {
      return 'Same day';
    } else if (result === 1) {
      return '1 day';
    } else if (result > 1) {
      return `${result} days`;
    } else {
      return `${Math.abs(result)} days (in the past)`;
    }
  };

  const getResultColor = () => {
    if (result === null) return 'text.secondary';
    if (result === 0) return 'info.main';
    if (result > 0) return 'success.main';
    return 'warning.main';
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        align="center" 
        sx={{ 
          mb: 4,
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700,
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        Date Calculator
      </Typography>

      <Card 
        elevation={4}
        sx={{
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.05)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Date Selection */}
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Start Date
                  </Typography>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                      setError('');
                      setResult(null);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "Select start date",
                        label: "Start Date",
                        'aria-label': "Select start date",
                        'aria-describedby': "start-date-description",
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                          '&:focus-within': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                          }
                        }
                      }
                    }}
                  />
                  <Typography id="start-date-description" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Choose the starting date for your calculation
                  </Typography>
                </Box>

                <IconButton
                  onClick={swapDates}
                  color="secondary"
                  size="large"
                  aria-label="Swap dates"
                  title="Swap start and end dates"
                  sx={{
                    bgcolor: 'secondary.main',
                    color: 'white',
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    boxShadow: 2,
                    alignSelf: 'flex-end',
                    '&:hover': {
                      bgcolor: 'secondary.dark',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'secondary.main',
                      outlineOffset: '2px',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0,
                    m: 0,
                  }}
                >
                  <SwapHoriz sx={{ fontSize: 32 }} />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    End Date
                  </Typography>
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => {
                      setEndDate(newValue);
                      setError('');
                      setResult(null);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "Select end date",
                        label: "End Date",
                        'aria-label': "Select end date",
                        'aria-describedby': "end-date-description",
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                          '&:focus-within': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                          }
                        }
                      }
                    }}
                  />
                  <Typography id="end-date-description" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Choose the ending date for your calculation
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Calculate />}
                  onClick={calculateDays}
                  disabled={!startDate || !endDate}
                  aria-label="Calculate days between dates"
                  aria-describedby="calculate-description"
                  sx={{
                    minWidth: 120,
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)',
                    },
                    '&:disabled': {
                      background: 'rgba(0, 0, 0, 0.12)',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    }
                  }}
                >
                  Calculate
                </Button>
                <Typography id="calculate-description" variant="caption" color="text.secondary" sx={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
                  {!startDate || !endDate ? 'Select both dates to enable calculation' : 'Calculate the number of days between the selected dates'}
                </Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={clearDates}
                  aria-label="Clear all dates and results"
                  sx={{
                    minWidth: 120,
                    borderColor: 'text.secondary',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'text.primary',
                      color: 'text.primary',
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'text.primary',
                      outlineOffset: '2px',
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>

              {/* Error Display */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Result Display */}
              {result !== null && !error && (
                <Box 
                  sx={{ 
                    mt: 2, 
                    p: 4, 
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    borderRadius: 3,
                    color: 'white',
                    textAlign: 'center',
                    boxShadow: '0 8px 32px rgba(37, 99, 235, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <CheckCircle sx={{ fontSize: 32, mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Days Between Dates
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: getResultColor() }}>
                    {getResultText()}
                  </Typography>
                  {startDate && endDate && (
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      From {format(startDate, 'MMMM d, yyyy')} to {format(endDate, 'MMMM d, yyyy')}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Additional Information */}
              {result !== null && !error && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Additional Information
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Card sx={{ p: 2, bgcolor: 'grey.50', flex: 1, minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Business sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Business Days
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Approximately {Math.floor(result! * 5/7)} days
                      </Typography>
                    </Card>
                    <Card sx={{ p: 2, bgcolor: 'grey.50', flex: 1, minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Schedule sx={{ fontSize: 20, mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Weeks
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {Math.floor(result! / 7)} weeks, {result! % 7} days
                      </Typography>
                    </Card>
                  </Box>
                </Box>
              )}
              
              {/* Live region for screen readers */}
              <Box
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  position: 'absolute',
                  left: '-10000px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden'
                }}
              >
                {result !== null && !error && `${getResultText()} between ${startDate && format(startDate, 'MMMM d, yyyy')} and ${endDate && format(endDate, 'MMMM d, yyyy')}`}
              </Box>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DateCalculator; 